import { useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildRecursiveString } from '../tools/utils';
var updateId = function (currentText, id) {
    return currentText.length ? "".concat(currentText, " ").concat(id) : id;
};
var useFormFieldHelper = function (_a) {
    var nativeDescribedBy = _a.nativeDescribedBy, errorNotification = _a.errorNotification, contextualHelp = _a.contextualHelp, characterCounter = _a.characterCounter, onDescribedByChange = _a.onDescribedByChange, onDescribedByTextChange = _a.onDescribedByTextChange;
    var uuid = useRef(uuidv4()).current;
    var errorMessageId = useRef("field-error-".concat(uuid)).current;
    var srErrorMessageId = useRef("field-sr-error-".concat(uuid)).current;
    var contextualHelpId = useRef("field-help-".concat(uuid)).current;
    var charCounterId = useRef("field-counter-".concat(uuid)).current;
    var contextualHelpText = useMemo(function () {
        if (!(contextualHelp === null || contextualHelp === void 0 ? void 0 : contextualHelp.content))
            return '';
        if (typeof contextualHelp.content === 'string') {
            return contextualHelp.content;
        }
        return buildRecursiveString(contextualHelp.content);
    }, [contextualHelp]);
    var displayError = useMemo(function () {
        return !!(errorMessageId.length &&
            ((errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message) || '').length &&
            (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.show));
    }, [errorMessageId, errorNotification]);
    var displaySrError = useMemo(function () {
        return !!(srErrorMessageId.length &&
            ((errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.srMessage) || '').length &&
            (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.show));
    }, [srErrorMessageId, errorNotification]);
    var displayHelpContent = useMemo(function () {
        return !!(contextualHelpId.length &&
            (contextualHelp === null || contextualHelp === void 0 ? void 0 : contextualHelp.content) &&
            contextualHelpText.length &&
            contextualHelp.show);
    }, [contextualHelpId, contextualHelpText, contextualHelp]);
    var displayCounter = useMemo(function () {
        return !!(charCounterId &&
            charCounterId.length &&
            typeof (characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.charCount) === 'number' &&
            typeof (characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.maxChars) === 'number' &&
            characterCounter.maxChars > 0 &&
            characterCounter.show);
    }, [charCounterId, characterCounter]);
    useEffect(function () {
        var describedBy = '';
        if (nativeDescribedBy) {
            describedBy = updateId(describedBy, nativeDescribedBy);
        }
        // should only add either the error id or sr error id
        if (displaySrError || (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customSrId)) {
            describedBy = updateId(describedBy, (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customSrId) || srErrorMessageId);
        }
        else if (displayError || (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customId)) {
            describedBy = updateId(describedBy, (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customId) || errorMessageId);
        }
        if (displayHelpContent || (contextualHelp === null || contextualHelp === void 0 ? void 0 : contextualHelp.customId)) {
            describedBy = updateId(describedBy, (contextualHelp === null || contextualHelp === void 0 ? void 0 : contextualHelp.customId) || contextualHelpId);
        }
        if (displayCounter || (characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.customId)) {
            describedBy = updateId(describedBy, (characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.customId) || charCounterId);
        }
        if (onDescribedByChange) {
            onDescribedByChange(describedBy.length ? describedBy : undefined);
        }
    }); // intentionally running useEffect on every render
    /**
     * The purpose of the onDescribedByText change function is to let implementing
     * components know what text is being rendered so that manual aria-label or
     * aria-live announcements can be made. This is generally only useful for
     * third-party libraries (e.g. react-dates).
     *
     * We're leaving out the CharacterCounter changes here because it would add
     * complexity for no immediate need. We don't attach the CharacterCounter to
     * any third-party libraries, and we handle its aria-live announcements
     * internally and within the component itself.
     */
    useEffect(function () {
        if (onDescribedByTextChange) {
            if ((displayError || displaySrError) && displayHelpContent) {
                onDescribedByTextChange("".concat(displaySrError
                    ? errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.srMessage
                    : errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message, ", ").concat(contextualHelpText));
            }
            else if (displaySrError) {
                onDescribedByTextChange(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.srMessage);
            }
            else if (displayError) {
                onDescribedByTextChange(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message);
            }
            else if (displayHelpContent) {
                onDescribedByTextChange(contextualHelpText);
            }
            else {
                onDescribedByTextChange(undefined);
            }
        }
    }, [
        displayError,
        displaySrError,
        displayHelpContent,
        errorNotification,
        contextualHelpText,
        onDescribedByTextChange,
    ]);
    return {
        displayError: displayError,
        displaySrError: displaySrError,
        displayHelpContent: displayHelpContent,
        displayCounter: displayCounter,
        errorMessageId: (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customId) || errorMessageId,
        srErrorMessageId: (errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customSrId) || srErrorMessageId,
        helpContentId: (contextualHelp === null || contextualHelp === void 0 ? void 0 : contextualHelp.customId) || contextualHelpId,
        charCounterId: (characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.customId) || charCounterId,
    };
};
export { useFormFieldHelper };
//# sourceMappingURL=useFormFieldHelper.js.map